import { useCallback, useEffect, useState } from 'react';
import {
  fetchSubmissions,
  updateSubmissionStatus,
  deleteSubmission,
  fetchDonations,
  updateDonationStatus,
  deleteDonation,
} from '@/lib/api';
import type { PrayerRequest, ContactSubmission, NewsletterSubscriber, Donation } from '@/lib/types';

type Tab = 'prayer' | 'contact' | 'newsletter' | 'donations';

export default function InboxPage() {
  const [tab, setTab] = useState<Tab>('prayer');
  const [prayers, setPrayers] = useState<PrayerRequest[]>([]);
  const [contacts, setContacts] = useState<ContactSubmission[]>([]);
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [donations, setDonations] = useState<Donation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, c, n, d] = await Promise.all([
        fetchSubmissions<PrayerRequest>('prayer_requests'),
        fetchSubmissions<ContactSubmission>('contact_submissions'),
        fetchSubmissions<NewsletterSubscriber>('newsletter_subscribers'),
        fetchDonations(),
      ]);
      setPrayers(p);
      setContacts(c);
      setSubscribers(n);
      setDonations(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tabs: Array<{ key: Tab; label: string; count: number }> = [
    { key: 'prayer', label: 'Prayer Requests', count: prayers.length },
    { key: 'contact', label: 'Contact Messages', count: contacts.length },
    { key: 'donations', label: 'Donations', count: donations.length },
    { key: 'newsletter', label: 'Subscribers', count: subscribers.length },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-xl text-foreground-900">Inbox & Requests</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Review prayer requests, contact messages and newsletter subscribers.
        </p>
      </div>

      <div className="flex gap-1 bg-background-100 rounded-full p-1 mb-6 w-fit">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
              tab === t.key ? 'bg-primary-500 text-white' : 'text-foreground-600'
            }`}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
          {error}
          <button type="button" onClick={load} className="ml-3 underline">
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl inline-block" aria-hidden="true" />
        </div>
      ) : (
        <>
          {tab === 'prayer' && <PrayerList rows={prayers} onChanged={load} />}
          {tab === 'contact' && <ContactList rows={contacts} onChanged={load} />}
          {tab === 'donations' && <DonationList rows={donations} onChanged={load} />}
          {tab === 'newsletter' && <NewsletterList rows={subscribers} onChanged={load} />}
        </>
      )}
    </div>
  );
}

const statusColors: Record<string, string> = {
  new: 'bg-accent-100 text-accent-900',
  read: 'bg-background-100 text-foreground-600',
  resolved: 'bg-green-100 text-green-700',
};

function PrayerList({ rows, onChanged }: { rows: PrayerRequest[]; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);

  const mark = async (id: string, status: string) => {
    setBusy(id);
    try {
      await updateSubmissionStatus('prayer_requests', id, status);
      onChanged();
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id: string) => {
    setBusy(id);
    try {
      await deleteSubmission('prayer_requests', id);
      onChanged();
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  };

  if (rows.length === 0) return <Empty text="No prayer requests yet." />;

  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.id} className="bg-white rounded-lg border border-background-200 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium text-foreground-900">
                {r.is_anonymous ? 'Anonymous' : r.name || 'Unknown'}
              </div>
              <div className="text-xs text-foreground-500 mt-0.5">
                {r.email && <span className="mr-3">{r.email}</span>}
                {r.phone && <span>{r.phone}</span>}
              </div>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[r.status] ?? statusColors.new}`}>
              {r.status}
            </span>
          </div>
          <p className="mt-3 text-sm text-foreground-700 whitespace-pre-wrap">{r.request}</p>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-foreground-400">{new Date(r.created_at).toLocaleString()}</span>
            <div className="flex gap-2">
              {r.status !== 'resolved' && (
                <button
                  type="button"
                  disabled={busy === r.id}
                  onClick={() => mark(r.id, 'resolved')}
                  className="px-3 py-1.5 rounded-md bg-green-100 text-green-700 text-xs font-medium hover:bg-green-200 cursor-pointer"
                >
                  Mark Resolved
                </button>
              )}
              {r.status === 'new' && (
                <button
                  type="button"
                  disabled={busy === r.id}
                  onClick={() => mark(r.id, 'read')}
                  className="px-3 py-1.5 rounded-md bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 cursor-pointer"
                >
                  Mark Read
                </button>
              )}
              <button
                type="button"
                disabled={busy === r.id}
                onClick={() => remove(r.id)}
                className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ContactList({ rows, onChanged }: { rows: ContactSubmission[]; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);

  const remove = async (id: string) => {
    setBusy(id);
    try {
      await deleteSubmission('contact_submissions', id);
      onChanged();
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  };

  if (rows.length === 0) return <Empty text="No contact messages yet." />;

  return (
    <div className="space-y-3">
      {rows.map((c) => (
        <div key={c.id} className="bg-white rounded-lg border border-background-200 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium text-foreground-900">{c.name || 'Unknown'}</div>
              <div className="text-xs text-foreground-500 mt-0.5">
                {c.email && <span className="mr-3">{c.email}</span>}
                {c.phone && <span>{c.phone}</span>}
              </div>
            </div>
            <button
              type="button"
              disabled={busy === c.id}
              onClick={() => remove(c.id)}
              className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 cursor-pointer"
            >
              Delete
            </button>
          </div>
          {c.subject && <div className="mt-2 text-sm font-medium text-foreground-800">{c.subject}</div>}
          <p className="mt-2 text-sm text-foreground-700 whitespace-pre-wrap">{c.message}</p>
          <div className="mt-3 text-xs text-foreground-400">{new Date(c.created_at).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

function NewsletterList({ rows, onChanged }: { rows: NewsletterSubscriber[]; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);

  const remove = async (id: string) => {
    setBusy(id);
    try {
      await deleteSubmission('newsletter_subscribers', id);
      onChanged();
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  };

  if (rows.length === 0) return <Empty text="No subscribers yet." />;

  return (
    <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background-100 text-left text-xs uppercase tracking-wider text-foreground-500">
              <th className="px-4 py-3 font-label font-semibold">Email</th>
              <th className="px-4 py-3 font-label font-semibold">Subscribed</th>
              <th className="px-4 py-3 font-label font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-background-100">
                <td className="px-4 py-3 text-foreground-700">{s.email}</td>
                <td className="px-4 py-3 text-foreground-500">{new Date(s.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    disabled={busy === s.id}
                    onClick={() => remove(s.id)}
                    className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 cursor-pointer"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DonationList({ rows, onChanged }: { rows: Donation[]; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);

  const mark = async (id: string, status: string) => {
    setBusy(id);
    try {
      await updateDonationStatus(id, status);
      onChanged();
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id: string) => {
    setBusy(id);
    try {
      await deleteDonation(id);
      onChanged();
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  };

  if (rows.length === 0) return <Empty text="No donations yet." />;

  return (
    <div className="space-y-3">
      {rows.map((d) => (
        <div key={d.id} className="bg-white rounded-lg border border-background-200 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium text-foreground-900">{d.full_name || 'Anonymous'}</div>
              <div className="text-xs text-foreground-500 mt-0.5">
                {d.email && <span className="mr-3">{d.email}</span>}
                <span className="mr-3">{d.amount}</span>
                <span>{d.payment_method}</span>
              </div>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[d.status] ?? statusColors.new}`}>
              {d.status}
            </span>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-foreground-400">{new Date(d.created_at).toLocaleString()}</span>
            <div className="flex gap-2">
              {d.status !== 'resolved' && (
                <button
                  type="button"
                  disabled={busy === d.id}
                  onClick={() => mark(d.id, 'resolved')}
                  className="px-3 py-1.5 rounded-md bg-green-100 text-green-700 text-xs font-medium hover:bg-green-200 cursor-pointer"
                >
                  Followed Up
                </button>
              )}
              {d.status === 'new' && (
                <button
                  type="button"
                  disabled={busy === d.id}
                  onClick={() => mark(d.id, 'read')}
                  className="px-3 py-1.5 rounded-md bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 cursor-pointer"
                >
                  Mark Read
                </button>
              )}
              <button
                type="button"
                disabled={busy === d.id}
                onClick={() => remove(d.id)}
                className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="py-16 text-center bg-white rounded-lg border border-background-200">
      <i className="ri-inbox-line text-4xl text-foreground-300" aria-hidden="true" />
      <p className="mt-3 text-sm text-foreground-500">{text}</p>
    </div>
  );
}